import React, { FunctionComponent, useCallback, useEffect, useEffectEvent, useState } from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';

import { PropertySelectField, useIsMounted } from '@paragon/client-tools';
import { ComponentDesignSummary } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueWarning } from '@paragon/notification-tools';

import {
  cancelComponentDesignImport,
  finishComponentDesignImport,
  previewComponentDesignForImport,
} from '../../../../store/actions/importComponentDesignActions';
import {
  getExistingComponentDesignNames,
  getUniqueComponentName,
} from '../../../../store/selectors/uniqueComponentNameSelectors';
import { closeImportComponentPanel } from '../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { TemplateComponentSelectField } from './TemplateComponentSelectField';

export const ImportComponentDesignPanel: FunctionComponent = () => {
  const dispatch = useAppDispatch();

  const info = useAppSelector((state) => state.application.importingComponentInfo)!;
  const otherComponentDesignNames = useAppSelector(getExistingComponentDesignNames);

  const isMounted = useIsMounted();

  const [isLoading, setIsLoading] = useState(true);
  const [templateSummaries, setTemplateSummaries] = useState<ComponentDesignSummary[] | null>(null);

  const [componentName, setComponentNameRaw] = useState<string | null>(null);

  const setComponentNameWithUniqueness = useCallback(
    (newName: string) => {
      const uniqueName = getUniqueComponentName(newName, otherComponentDesignNames);
      setComponentNameRaw(uniqueName);
    },
    [otherComponentDesignNames],
  );

  const onChangePreviewComponent = useCallback(
    (newPreviewComponentDesignGuid: string) => {
      setIsLoading(true);
      dispatch(previewComponentDesignForImport(newPreviewComponentDesignGuid)).then((componentDesign) => {
        if (!isMounted()) return;

        setComponentNameWithUniqueness(componentDesign.component.name);

        setIsLoading(false);
      });
    },
    [dispatch, isMounted, setComponentNameWithUniqueness],
  );

  const onChangePreviewComponentEffectEvent = useEffectEvent(onChangePreviewComponent);

  useEffect(() => {
    designService.componentDesigns.getComponentDesignTemplateSummaries().then((summaries) => {
      if (!isMounted()) return;

      if (summaries.length === 0) {
        dispatch(enqueueWarning('No component designs found in template project.'));
        return;
      }

      setTemplateSummaries(summaries);
      onChangePreviewComponentEffectEvent(summaries[0].guid);
    });
  }, [dispatch, isMounted]);

  return (
    <Box sx={{ p: 2, mb: 9, display: 'flex', flexDirection: 'column' }}>
      <Typography align="center" variant="h6">
        Import Component Design
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box
        sx={{
          '& > *': {
            mb: 2,
          },
        }}
      >
        <PropertySelectField
          disabled
          property="Template Project"
          value="Component Templates"
          options={['Component Templates']}
        />

        <TemplateComponentSelectField
          loading={isLoading}
          summaries={templateSummaries ?? []}
          value={info.templateComponentGuid ?? ''}
          onChange={onChangePreviewComponent}
        />

        <TextField
          variant="standard"
          fullWidth
          disabled={isLoading || !info.templateComponentGuid}
          label="Component Name"
          value={componentName ?? 'Loading...'}
          onChange={(event) => void setComponentNameRaw(event.target.value)}
          onBlur={(event) => void setComponentNameWithUniqueness(event.target.value)}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          sx={{ width: '40%', m: 1 }}
          disabled={isLoading || !info.templateComponentGuid || !componentName}
          onClick={async () => {
            if (info.templateComponentGuid) {
              await dispatch(finishComponentDesignImport(componentName!));
            }
          }}
        >
          Import
        </Button>
        <Button
          variant="contained"
          sx={{ width: '40%', m: 1 }}
          onClick={async () => {
            if (info.templateComponentGuid) {
              await dispatch(cancelComponentDesignImport(info.templateComponentGuid));
            } else {
              dispatch(closeImportComponentPanel());
            }
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
